import { AnalyticsProvider } from '~/utils/analytics-provider';

/**
 * Hotjar Analytics Provider
 * Professional implementation following provider interface
 */
export default class HotjarProvider extends AnalyticsProvider {
  constructor() {
    super('Hotjar');
    this.hotjarLoaded = false;
  }

  /**
   * Initialize Hotjar
   */
  initialize(config, propertyContext) {
    super.initialize(config, propertyContext);

    if (typeof window === 'undefined') {
      this.error('Cannot initialize Hotjar in SSR environment');
      return;
    }

    this.loadHotjarScript();
  }

  /**
   * Load Hotjar script
   */
  loadHotjarScript() {
    if (window.hj) {
      this.hotjarLoaded = true;
      return;
    }

    (function(h,o,t,j,a,r){
      h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
      h._hjSettings={hjid:this.config.site_id,hjsv:6};
      a=o.getElementsByTagName('head')[0];
      r=o.createElement('script');r.async=1;
      r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
      a.appendChild(r);
    }.bind(this))(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');

    this.hotjarLoaded = true;
    this.log('Script loaded successfully');
  }

  /**
   * Track events using Hotjar
   */
  track(eventName, eventData) {
    if (!this.isReady() || !window.hj) {
      this.error('Provider not ready or hj not available');
      return;
    }

    const hotjarEvent = this.mapEvent(eventName);
    const hotjarData = this.formatEventData(eventName, eventData);

    try {
      if (hotjarData) {
        window.hj('event', hotjarEvent);
        window.hj('identify', null, hotjarData);
      } else {
        window.hj('event', hotjarEvent);
      }

      this.log(`Event tracked: ${hotjarEvent}`, hotjarData);
    } catch (error) {
      this.error('Failed to track event:', error);
    }
  }

  /**
   * Map standard events to Hotjar events
   */
  mapEvent(standardEvent) {
    const hotjarEventMap = {
      'page_view': 'page_view',
      'view_item': 'view_accommodation',
      'add_to_cart': 'add_to_cart',
      'remove_from_cart': 'remove_from_cart',
      'begin_checkout': 'begin_checkout',
      'add_payment_info': 'add_payment_info',
      'purchase': 'booking_completed',
      'search': 'search_accommodation',
      'view_item_list': 'view_room_list'
    };

    return hotjarEventMap[standardEvent] || standardEvent;
  }

  /**
   * Format event data for Hotjar
   */
  formatEventData(eventName, eventData) {
    const ecommerce = this.extractEcommerceData(eventData);

    switch (eventName) {
      case 'purchase':
        return {
          property_name: this.propertyContext?.property_name,
          booking_value: ecommerce.value || 0,
          currency: ecommerce.currency || 'EUR',
          room_count: ecommerce.items?.length || 0
        };

      case 'begin_checkout':
        return {
          property_name: this.propertyContext?.property_name,
          cart_value: ecommerce.value || 0,
          currency: ecommerce.currency || 'EUR'
        };

      case 'search':
        return {
          search_term: eventData.search_term || '',
          property_name: this.propertyContext?.property_name
        };

      default:
        return {
          property_name: this.propertyContext?.property_name
        };
    }
  }

  /**
   * Get provider type for consent checking
   */
  getProviderType() {
    return 'experience'; // Hotjar is for user experience analysis
  }

  /**
   * Check if provider is ready
   */
  isReady() {
    return super.isReady() && this.hotjarLoaded && window.hj;
  }
}